import { useSyncExternalStore } from 'react';

/**
 * The clinic's own time zone, from `/portal/config`. Appointment instants are
 * UTC on the wire; rendering them in the browser's zone shows a travelling
 * patient a time nobody at the clinic would recognise. Mirrors the app's
 * `portal/clinicClock`.
 */
let clinicTimeZone: string | null = null;
const listeners = new Set<() => void>();

export function setClinicTimeZone(next?: string | null) {
  const value = next && next.trim() ? next.trim() : null;
  if (value === clinicTimeZone) return;
  clinicTimeZone = value;
  for (const l of listeners) l();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

const get = () => clinicTimeZone;

export function useClinicTimeZone(): string | null {
  return useSyncExternalStore(subscribe, get, get);
}

/** Reads the current clinic zone outside React (grid maths, sort keys). */
export function getClinicTimeZone(): string | null {
  return clinicTimeZone;
}

/**
 * Minutes since midnight **in the clinic's zone**.
 *
 * Anything that lines appointment times up in a column — a row key, a sort
 * order — has to agree with what the label says, and labels render in the
 * clinic's zone. Using the browser's `getHours()` instead silently works
 * wherever the two zones share a calendar day and then scrambles the order
 * when they do not: a Dubai clinic viewed from Los Angeles puts its 9:00 AM
 * slots on the previous browser day, so they sort BELOW the afternoon.
 */
export function minutesInClinicZone(value: string | Date): number {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 0;
  const zone = clinicTimeZone;
  if (!zone) return date.getHours() * 60 + date.getMinutes();
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  // 24:00 is a legal en-GB rendering of midnight; the row belongs at 0.
  return (hour % 24) * 60 + minute;
}

/**
 * `YYYY-MM-DD` **in the clinic's zone**.
 *
 * The day-column twin of `minutesInClinicZone`: an instant belongs to the day
 * the clinic says it does. Bucketing by the browser's calendar instead drops a
 * Dubai clinic's 9:00 AM visit into the previous column for a patient in Los
 * Angeles — or out of the grid entirely, when that column is off-screen.
 */
export function dayKeyInClinicZone(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const zone = clinicTimeZone;
  if (!zone) {
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }
  // en-CA renders ISO-shaped dates, which is exactly the key format wanted.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}
